import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Api
import client from '../../api/client';
// Constants
import { CompanyName } from '../../utils/Constants';
import { NEWSLETTER_UNSUBSCRIBE_API } from '../../utils/ApiRoutes';
// Data
import {
  newsletterUnsubscribeAdditionalMeta,
  newsletterUnsubscribeStructuredData,
} from '../../utils/data/MetaData';
// Components
import Navbar from '../../components/nav/Navbar';
import { HelmetItem } from '../../components/utils/HelmetItem';
import LoadingSpinner from '../../components/utils/LoadingSpinner';
import GeneralHeader from '../../components/header/GeneralHeader';
import PageDivider from '../../components/gallery/PageDivider';

function NewsletterUnsubscribePage() {
  const { subId, uniqueString } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [retryStatus, setRetryStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [hideNavbar, setHideNavbar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById(
        'scroll-section-newsletter-unsubscribe'
      );
      if (!section) return;

      const sectionBottom = section.getBoundingClientRect().bottom;
      setHideNavbar(sectionBottom <= 130);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!subId || !uniqueString) {
      setStatus('error');
      return;
    }

    client
      .delete(`${NEWSLETTER_UNSUBSCRIBE_API}/${subId}/${uniqueString}`, false)
      .then((res) => {
        setStatus('success');
      })
      .catch((err) => {
        console.error('Failed to process unsubscribe:', err);
        setStatus('error');
      });
  }, [subId, uniqueString]);

  const handleRetryUnsubscribe = async () => {
    setRetryStatus('loading');

    client
      .delete(`${NEWSLETTER_UNSUBSCRIBE_API}/${subId}/${uniqueString}`, false)
      .then(() => {
        setStatus('success');
      })
      .catch((err) => {
        console.error('Failed to unsubscribe:', err);
        setStatus('error');
      })
      .finally(() => {
        setRetryStatus(null);
      });
  };

  return (
    <>
      <HelmetItem
        PageName='Unsubscribe - Newsletter'
        desc={`Unsubscribe from the ${CompanyName} newsletter. Manage your preferences and stop receiving updates.`}
        keywords={`unsubscribe, ${CompanyName} newsletter, stop emails, email preferences`}
        additionalMeta={newsletterUnsubscribeAdditionalMeta}
        structuredData={newsletterUnsubscribeStructuredData}
      />

      <div className='grid grid-rows-reg min-h-screen font-main bg-colour1 dark:bg-colour1 text-colour2 dark:text-colour2 overflow-hidden'>
        {/* Nav + header section */}
        <div
          id='scroll-section-newsletter-unsubscribe'
          className='grid bg-colour4 dark:bg-colour4 full_shadow_black_invert min-h-[45vh] max-h-[45vh]'
        >
          {/* Navigation */}
          <Navbar isHidden={hideNavbar} />

          {/* Divider */}
          <PageDivider />

          {/* Header */}
          <GeneralHeader
            page={`Newsletter`}
            title={`Manage Your Subscription Preferences`}
          />
        </div>

        <main className='grid place-items-center px-4 pt-24 pb-24' role='main'>
          <header className='grid w-full' role='banner'>
            <div className='grid gap-y-4 w-full px-8 text-center lg:container lg:mx-auto py-6'>
              <h1 className='text-xl font-semibold'>
                Unsubscribe Confirmation
              </h1>
              <p className='text-sm text-gray-700 mt-2 max-w-2xl mx-auto'>
                This page processes your request to unsubscribe from the{' '}
                {CompanyName} newsletter. <br />
                We’re sorry to see you go, but you can always resubscribe at any
                time.
              </p>
            </div>
          </header>

          <section
            className='grid gap-3 text-center'
            role='status'
            aria-live='polite'
            aria-busy={status === 'loading' || retryStatus === 'loading'}
          >
            {status === 'loading' && (
              <>
                <p className='mb-2'>
                  Processing your unsubscribe request, please wait...
                </p>
                <div className='flex justify-center mx-auto'>
                  <LoadingSpinner lg={true} />
                </div>
              </>
            )}

            {status === 'success' && (
              <>
                <p className='text-green-600 font-medium'>
                  ✅ You have been successfully unsubscribed!
                </p>
                <p className='text-gray-800 text-sm max-w-md'>
                  You will no longer receive emails from the {CompanyName}{' '}
                  newsletter.
                </p>
                <p className='text-gray-600 text-sm'>
                  If this was a mistake or you change your mind, you’re always
                  welcome to subscribe again.
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <p className='text-red-600 font-medium'>
                  ❌ Unsubscribe request failed or the link is invalid/expired.
                </p>
                <p className='text-gray-800 text-sm max-w-md'>
                  If you believe this is an error, you can retry the unsubscribe
                  request below.
                </p>
                <button
                  onClick={handleRetryUnsubscribe}
                  disabled={retryStatus === 'loading'}
                  className='text-sm text-blue-600 underline hover:text-blue-800 disabled:opacity-50'
                  aria-label='Retry unsubscribe request'
                >
                  {retryStatus === 'loading' ? (
                    <div className='flex justify-center mx-auto'>
                      <LoadingSpinner xs={true} />
                    </div>
                  ) : (
                    'Retry unsubscribe'
                  )}
                </button>
                {retryStatus === 'success' && (
                  <p className='text-green-600 text-sm'>
                    ✅ Your unsubscribe request was successful.
                  </p>
                )}
                {retryStatus === 'error' && (
                  <p className='text-red-600 text-sm'>
                    ❌ Failed to process unsubscribe. Please try again shortly.
                  </p>
                )}
              </>
            )}
          </section>
        </main>
      </div>
    </>
  );
}

export default NewsletterUnsubscribePage;
